import { useMemo, useState, type ReactNode } from "react";
import { AlertCircle, QrCode, SearchX } from "lucide-react";
import {
  Banner,
  Button,
  Card,
  Chip,
  DataTable,
  EmptyState,
  Kpi,
  Page,
  PageHeader,
  Segmented,
  SidePanel,
  Toolbar,
  type DataTableColumn,
  type SegmentedOption,
} from "@/components/ds";
import JoinLinkDetail from "@/components/join-links/JoinLinkDetail";
import { DEFAULT_JOIN_LINK_PRESET, joinLinkRange, type JoinLinkPreset } from "@/components/join-links/joinLinkRange";
import { formatCount, num, phoneOpens, seatsLabel, splitLinkLabel } from "@/components/join-links/joinLinkFormat";
import { useJoinLinks } from "@/hooks/useJoinLinks";
import type { JoinLinkSummaryResponse } from "@/types/generated/cms.generated";

const PERIOD_OPTIONS: SegmentedOption[] = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "all", label: "All time" },
];

const NO_OPENS_TITLE = "No opens yet";
const NO_OPENS_TEXT = "Every company has a link from the day it's created. Opens and joins appear here once someone uses one.";

const hasActivity = (row: JoinLinkSummaryResponse) =>
  num(row.opens?.ios) + num(row.opens?.android) + num(row.opens?.other) + num(row.opens?.preview) > 0 ||
  num(row.employeesJoined) + num(row.guestsJoined) > 0;

/** "1 company over its seats" / "N companies over their seats". */
const overSeatsNote = (count: number) =>
  count === 1 ? "1 company over its seats" : `${formatCount(count)} companies over their seats`;

const countLabel = (count: number) => `${formatCount(count)} ${count === 1 ? "company" : "companies"} · click a row for details`;

/** A header that may wrap onto two lines (the verbatim string is unchanged), so seven columns fit at 1280 px. */
const Wrap = ({ children }: { children: ReactNode }) => (
  <span className="inline-block min-w-[64px] whitespace-normal">{children}</span>
);

const Small = ({ children }: { children: ReactNode }) => (
  <small className="block font-ds text-[11.5px] font-normal text-ds-ink3">{children}</small>
);

const columns: DataTableColumn<JoinLinkSummaryResponse>[] = [
  {
    id: "company",
    header: "Company",
    cell: (r) => (
      <span className="flex flex-wrap items-center gap-1.5">
        <b className="font-semibold text-ds-ink">{r.name}</b>
        {/* The app follows the organisation's package, not its white-label flag. */}
        {r.isAthlynApp !== true && r.appName && <Chip tone="info">{r.appName}</Chip>}
        {r.available !== true && <Chip tone="warn">No app yet</Chip>}
      </span>
    ),
  },
  {
    id: "link",
    header: "Link",
    // The host part truncates; the /join/<subdomain> tail never does. Full link in the title + the side panel.
    cell: (r) => {
      const { head, tail } = splitLinkLabel(r.joinUrl);
      return (
        <span
          className="flex max-w-[220px] font-ds-mono text-[12.5px] text-ds-ink2"
          title={r.joinUrl ?? undefined}
          data-testid="join-link-cell"
        >
          {head && <span className="min-w-0 truncate">{head}</span>}
          <span className="shrink-0 whitespace-nowrap">{tail}</span>
        </span>
      );
    },
  },
  {
    id: "opens",
    header: <Wrap>Phone opens</Wrap>,
    align: "right",
    cell: (r) => (
      <span className="whitespace-nowrap font-ds-mono text-ds-ink">
        {formatCount(phoneOpens(r))}
        <Small>
          {formatCount(r.opens?.ios)} iPhone · {formatCount(r.opens?.android)} Android
        </Small>
      </span>
    ),
  },
  {
    id: "joined",
    header: <Wrap>Employees joined</Wrap>,
    align: "right",
    cell: (r) => (
      <span className="whitespace-nowrap font-ds-mono text-ds-ink">
        {formatCount(r.employeesJoined)}
        {num(r.guestsJoined) > 0 && (
          <Small>
            + {formatCount(r.guestsJoined)} {num(r.guestsJoined) === 1 ? "guest" : "guests"}
          </Small>
        )}
      </span>
    ),
  },
  {
    id: "seats",
    header: <Wrap>Seats used</Wrap>,
    align: "right",
    cell: (r) => <span className="whitespace-nowrap font-ds-mono text-ds-ink">{seatsLabel(r)}</span>,
  },
  {
    id: "no-seat",
    header: "No seat",
    align: "right",
    cell: (r) =>
      num(r.signedUpNoSeat) > 0 ? (
        <Chip tone="warn" className="font-ds-mono">
          {formatCount(r.signedUpNoSeat)}
        </Chip>
      ) : (
        <span className="font-ds-mono text-ds-ink3">0</span>
      ),
  },
  {
    id: "before-launch",
    header: <Wrap>Before launch (estimate)</Wrap>,
    align: "right",
    cell: (r) => (
      <span className="font-ds-mono text-ds-ink3">{num(r.beforeLaunch) > 0 ? formatCount(r.beforeLaunch) : "—"}</span>
    ),
  },
];

/** Join links — every company's app download link: opens, joins, seats and its QR (Athlyn staff only). */
export default function JoinLinksPage() {
  const [preset, setPreset] = useState<JoinLinkPreset>(DEFAULT_JOIN_LINK_PRESET);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<JoinLinkSummaryResponse | null>(null);
  // Recomputed per render: a page left open past UAE midnight picks up the new day on the next change.
  const range = joinLinkRange(preset);
  const list = useJoinLinks(range);

  const rows = useMemo(() => list.data ?? [], [list.data]);
  // Row keys: the organisation id, else the row's place in the server list (unique, and stable
  // while searching because the filtered rows are the same objects).
  const rowKeys = useMemo(
    () => new Map(rows.map((r, i) => [r, r.organizationId ?? `row-${i}`] as const)),
    [rows]
  );

  const totals = useMemo(
    () =>
      rows.reduce(
        (sum, r) => ({
          phone: sum.phone + phoneOpens(r),
          employees: sum.employees + num(r.employeesJoined),
          guests: sum.guests + num(r.guestsJoined),
          noSeat: sum.noSeat + num(r.signedUpNoSeat),
          overSeats: sum.overSeats + (num(r.signedUpNoSeat) > 0 ? 1 : 0),
          other: sum.other + num(r.opens?.other),
          preview: sum.preview + num(r.opens?.preview),
          isTest: sum.isTest || r.isTestLink === true,
          active: sum.active || hasActivity(r),
        }),
        { phone: 0, employees: 0, guests: 0, noSeat: 0, overSeats: 0, other: 0, preview: 0, isTest: false, active: false }
      ),
    [rows]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.name, r.subdomain].some((v) => v?.toLowerCase().includes(q)));
  }, [rows, search]);

  const ready = list.isSuccess;
  const value = (n: number) => (ready ? formatCount(n) : "–");

  return (
    <Page data-testid="join-links-page">
      <PageHeader
        title="Join links"
        subtitle="Each company's app download link — how often it's opened and how many people joined the app. Opens are not people."
        actions={
          <>
            {totals.isTest && <Chip tone="warn">Dev — test system</Chip>}
            <Segmented label="Period" options={PERIOD_OPTIONS} value={preset} onChange={(v) => setPreset(v as JoinLinkPreset)} />
          </>
        }
      />

      {list.isError ? (
        <Banner
          tone="err"
          icon={AlertCircle}
          action={
            <Button size="sm" variant="secondary" onClick={() => void list.refetch()}>
              Try again
            </Button>
          }
        >
          <b>Couldn't load join links.</b> Check your connection and try again.
        </Banner>
      ) : (
        <>
          <div className="mb-3.5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="join-links-kpis">
            <Kpi label="Phone opens" value={value(totals.phone)} note="iPhone + Android" noteTone="muted" />
            <Kpi label="Employees joined" value={value(totals.employees)} note="first app sign-in" noteTone="muted" />
            <Kpi label="Guests joined" value={value(totals.guests)} note="companies that allow guests" noteTone="muted" />
            <Kpi
              label="Signed up, no seat"
              value={value(totals.noSeat)}
              note={ready ? overSeatsNote(totals.overSeats) : undefined}
              noteTone={totals.overSeats > 0 ? "err" : "muted"}
            />
          </div>

          <Card>
            {ready && rows.length > 0 && !totals.active && (
              <div className="border-b border-ds-line" data-testid="join-links-no-activity">
                <EmptyState icon={QrCode} title={NO_OPENS_TITLE} text={NO_OPENS_TEXT} />
              </div>
            )}
            <Toolbar
              search={{ value: search, onChange: setSearch, placeholder: "Search companies…", label: "Search companies" }}
              count={ready ? countLabel(filtered.length) : undefined}
            />
            <DataTable
              label="Join links"
              columns={columns}
              rows={filtered}
              getRowId={(r) => rowKeys.get(r) ?? ""}
              onRowClick={setSelected}
              isLoading={list.isLoading}
              empty={
                rows.length === 0 ? (
                  <EmptyState icon={QrCode} title={NO_OPENS_TITLE} text={NO_OPENS_TEXT} />
                ) : (
                  <EmptyState
                    icon={SearchX}
                    title="Nothing matches this search."
                    action={{ label: "Clear search", onClick: () => setSearch("") }}
                  />
                )
              }
            />
          </Card>

          {ready && rows.length > 0 && (
            <p className="mt-2.5 text-xs text-ds-ink3" data-testid="join-links-footer">
              Not counted as phone opens: computers &amp; link scanners ({formatCount(totals.other)}) and link previews (
              {formatCount(totals.preview)})
            </p>
          )}
        </>
      )}

      <SidePanel open={selected !== null} onOpenChange={(open) => !open && setSelected(null)} title={selected?.name ?? ""}>
        {selected && <JoinLinkDetail row={selected} range={range} onClose={() => setSelected(null)} />}
      </SidePanel>
    </Page>
  );
}
